// A PAGE LOADS WITH ITS FIRST RENDER: its code, and the stylesheets it imports,
// are a chunk of their own (Vite emits each chunk's CSS beside it), so the
// first paint carries the frame and the home, never every page's CSS. Where a
// lazy page stands, <PageSuspense> holds its place until it arrives, and a
// view transition waits for it (pagesLoaded), so a morph's new state is the
// page and never the gap before it.
import { Suspense, lazy, useLayoutEffect, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react';
import { isStaleChunk, reloadForNewBuild } from './staleBuild';

/** A module's named component, loaded the first time it renders. Code a
 *  deploy has replaced reloads the page for the new build, once (lib/staleBuild);
 *  the page holds its place meanwhile. */
export function lazyNamed<M extends Record<K, ComponentType<any>>, K extends keyof M>(load: () => Promise<M>, name: K): LazyExoticComponent<M[K]> & { preload: () => Promise<unknown> } {
  // `preload` fetches the page's code ahead of its first opening, so that opening is instant (the module loads once)
  return Object.assign(lazy(() => load().then(
    (module) => ({ default: module[name] }),
    (error) => (isStaleChunk(error) && reloadForNewBuild() ? new Promise<never>(() => {}) : Promise.reject(error)),
  )), { preload: load });
}

let loading = 0;
const waiting = new Set<() => void>();

/** Stands while a page loads; counted, so a transition can wait it out. */
function Loading() {
  useLayoutEffect(() => {
    loading += 1;
    return () => {
      loading -= 1;
      if (loading) return;
      for (const done of waiting) done();
      waiting.clear();
    };
  }, []);
  return null;
}

/** Where a lazy page stands: nothing until it is there. */
export function PageSuspense({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/** Resolves once no page is still loading. */
export const pagesLoaded = (): Promise<void> => (loading ? new Promise((resolve) => { waiting.add(resolve); }) : Promise.resolve());
